import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, onToggle }) => (
  <div className="border-2 border-[#b5badd] rounded-lg bg-white overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-6 lg:p-8 text-left hover:bg-gray-50 transition-colors"
    >
      <span className="font-proxima-bold font-bold text-ebony-clay text-base lg:text-lg tracking-[0.8px] leading-normal pr-4">
        Q: {question}
      </span>
      <svg
        className={`flex-shrink-0 w-5 h-5 text-royal-blue transition-transform ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && (
      <div className="px-6 lg:px-8 pb-6 lg:pb-8 pt-0">
        <p className="font-proxima text-black text-base lg:text-lg tracking-[0.8px] leading-relaxed">
          {answer}
        </p>
      </div>
    )}
  </div>
);

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number>(0);

  const faqs = [
    {
      question: 'How does the 3-day quotation process work?',
      answer: "Once you submit your requirements, our AI analyzes them instantly, followed by expert review. Within 3 business days, you'll receive a comprehensive quotation.",
    },
    {
      question: 'What is Document Centric AI Development™?',
      answer: 'Our proprietary approach ensures every feature is fully documented before development begins. This eliminates surprises and ensures the final product matches your exact vision.',
    },
    {
      question: 'Can you work with existing projects?',
      answer: 'Absolutely! We specialize in taking over troubled projects, modernizing legacy systems, and improving existing codebases. We can audit, fix, and enhance your current solutions.',
    },
    {
      question: 'What technologies do you use?',
      answer: 'We use modern, enterprise-grade technologies including React, Node.js, Python, cloud platforms (AWS, Azure, GCP), and AI/ML frameworks. We choose the best tech stack for your specific needs.',
    },
    {
      question: 'Do you provide ongoing support after launch?',
      answer: 'Yes! We offer comprehensive maintenance packages, ongoing support, and continuous improvements to ensure your solution stays secure, fast, and up-to-date.',
    },
    {
      question: 'How do you ensure code quality?',
      answer: 'Every line of code is peer-reviewed, follows best practices, includes comprehensive documentation, and undergoes rigorous testing before deployment.',
    },
  ];

  return (
    <div className="w-full bg-white">
      <Navbar />

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-content mx-auto px-6">
          <h1 className="font-exo font-bold text-ebony-clay text-3xl lg:text-[48px] tracking-[2.4px] leading-tight mb-12 lg:mb-16">
            FAQ
          </h1>

          {/* FAQ Items */}
          <div className="space-y-4 lg:space-y-6">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA - copied from BookMeetingCTA */}
      <section className="bg-royal-blue py-12 lg:py-16 relative overflow-hidden">
        {/* Decorative circles — hidden on mobile */}
        <div className="absolute left-4 lg:left-[227px] top-1/2 -translate-y-1/2 w-[336px] h-[336px] pointer-events-none hidden lg:block">
          <div
            className="absolute inset-0 rounded-full opacity-5"
            style={{ background: 'radial-gradient(50% 50% at -29% 62%, rgba(137,146,208,1) 3%, rgba(255,255,255,1) 100%)' }}
          />
          <div
            className="absolute left-[44px] top-[44px] w-[248px] h-[248px] rounded-full opacity-5"
            style={{ background: 'radial-gradient(50% 50% at -29% 62%, rgba(137,146,208,1) 3%, rgba(255,255,255,1) 100%)' }}
          />
          <div className="absolute left-[72px] top-[69px] w-[197px] h-[197px] rounded-full bg-[#4452e340] blur-[18px]" />
        </div>

        <div className="max-w-content mx-auto px-6 flex flex-col lg:flex-row items-center gap-8 lg:gap-12 relative z-10">
          {/* Text content — left side */}
          <div className="flex-1">
            <h2 className="font-exo font-black text-white text-3xl lg:text-[42px] tracking-[2.1px] leading-tight">
              Still Have Questions?
            </h2>
            <p className="font-proxima text-white text-lg lg:text-2xl tracking-[1.2px] leading-normal mt-6">
              Our team is here to help
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-md overflow-hidden bg-white cursor-pointer py-3 px-5 lg:py-4 lg:px-6 hover:bg-opacity-90 transition-colors mt-8"
            >
              <span className="font-proxima-bold font-bold text-royal-blue text-base lg:text-2xl leading-6 whitespace-nowrap">
                CONTACT US
              </span>
            </Link>
          </div>

          {/* Meeting photo — right side */}
          <div className="flex-shrink-0 w-full max-w-[220px] lg:max-w-[425px] mx-auto lg:mx-0">
            <img
              className="w-full h-auto object-cover rounded"
              src={require('../assets/img/book-meeting/meeting-photo.png')}
              alt="Team meeting"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
